"use client"

import { useState, type FormEvent } from "react"
import { ArrowRight, Eye, EyeOff, LoaderCircle, LockKeyhole, UserRound } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export function LoginForm({ next }: { next: string }) {
  const [visible, setVisible] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    const data = new FormData(event.currentTarget)
    setPending(true)
    setError("")
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userName: data.get("userName"), userPassword: data.get("userPassword") }),
      })
      const result = await response.json().catch(() => null)
      if (!response.ok) throw new Error(response.status === 429
        ? "Demasiados intentos. Esperá unos minutos antes de volver a ingresar."
        : result?.error || "No se pudo iniciar sesión. Intentá nuevamente.")
      window.location.replace(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo conectar. Intentá nuevamente.")
      setPending(false)
    }
  }
  return (
    <form onSubmit={submit} className="space-y-5" aria-busy={pending}>
      <div className="space-y-2">
        <Label htmlFor="userName">Usuario de Clayer</Label>
        <div className="relative">
          <UserRound aria-hidden="true" className="pointer-events-none absolute top-3.5 left-3.5 size-4 text-muted-foreground" />
          <Input id="userName" name="userName" autoComplete="username" autoCapitalize="none" spellCheck={false} required maxLength={150} disabled={pending} placeholder="Tu usuario" className="h-11 pl-10" aria-describedby={error ? "login-error" : undefined} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="userPassword">Contraseña</Label>
        <div className="relative">
          <LockKeyhole aria-hidden="true" className="pointer-events-none absolute top-3.5 left-3.5 size-4 text-muted-foreground" />
          <Input id="userPassword" name="userPassword" type={visible ? "text" : "password"} autoComplete="current-password" required maxLength={256} disabled={pending} placeholder="Contraseña" className="h-11 pr-11 pl-10" aria-describedby={error ? "login-error" : undefined} />
          <button type="button" onClick={() => setVisible(!visible)} aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"} aria-pressed={visible} className="absolute top-0 right-0 flex size-11 items-center justify-center rounded-2xl text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
      </div>
      {error && <p id="login-error" role="alert" className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={pending} className="h-11 w-full">
        {pending ? <><LoaderCircle className="size-4 animate-spin" /> Validando acceso…</> : <>Ingresar a Analítica <ArrowRight className="ml-2 size-4" /></>}
      </Button>
    </form>
  )
}
